'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { Reveal } from '@/rsc/client';

import { ActiveSlideContext, SlideIndexContext } from './product-slide.client';

type Props = {
  /** Server-rendered cards, one per product (each root element is the scroll-snap card). */
  slides: ReactNode[];
  variant: 'dark' | 'light';
};

// Full-bleed rail: pull out to the viewport edges on both sides (CONTENT_GUTTER_NEGATIVE /
// CONTENT_GUTTER as literals for Panda's static extraction) and pad back in, so cards snap to the
// content line while scrolled cards stay visible through both gutters.
const trackClass = css({
  marginX: {
    base: 'calc(-1 * max(20px, calc((100vw - 1920px) / 2 + 20px)))',
    sm: 'calc(-1 * max(24px, calc((100vw - 1920px) / 2 + 24px)))',
    lg: 'calc(-1 * max(80px, calc((100vw - 1920px) / 2 + 80px)))',
    '3xl': 'calc(-1 * max(96px, calc((100vw - 1920px) / 2 + 96px)))',
  },
  paddingX: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  overflowX: 'auto',
  mb: 6,
  scrollSnapType: 'x mandatory',
  scrollPaddingLeft: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  scrollPaddingRight: {
    base: 'max(20px, calc((100vw - 1920px) / 2 + 20px))',
    sm: 'max(24px, calc((100vw - 1920px) / 2 + 24px))',
    lg: 'max(80px, calc((100vw - 1920px) / 2 + 80px))',
    '3xl': 'max(96px, calc((100vw - 1920px) / 2 + 96px))',
  },
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});
const rowClass = css({ display: 'flex', gap: 0 });

// SliderControls (functional/SliderControls) without Chakra: Flex end/end + two v2 Buttons
// (`label=""`, icon only, iconSpacing 0) with the sliderLight/sliderDark variants; the section
// passed `sx={{ '& svg path': { fill: 'white' } }}` on the dark variant.
const controlsClass = css({ display: 'flex', justifyContent: 'flex-end', alignItems: 'end' });
const controlsDarkClass = css({ '& svg path': { fill: 'white' } });
const arrowClass = css({ display: 'inline-flex', alignSelf: 'center', flexShrink: 0 });
const enabledClass = css({ opacity: 1, cursor: 'pointer' });
const disabledClass = css({ opacity: 0.3, cursor: 'not-allowed' });
const nextMarginClass = css({ ml: 4 });

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M3.828 6.99968H16V8.99968H3.828L9.192 14.3637L7.778 15.7777L0 7.99968L7.778 0.22168L9.192 1.63568L3.828 6.99968Z"
      fill="#5F2DD3"
    />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M12.172 6.99968L6.808 1.63568L8.222 0.22168L16 7.99968L8.222 15.7777L6.808 14.3637L12.172 8.99968H0V6.99968H12.172Z"
      fill="#5F2DD3"
    />
  </svg>
);

/**
 * Client island of IndustriesProductsSection: the scroll-snap track (cards are server slots) and
 * the prev/next controls. Native horizontal scroll + CSS scroll-snap; `current` is derived from
 * the scroll position and only drives which slide's media plays (via context). Edge state is
 * measured inside a ResizeObserver callback (no eager layout read during hydration).
 */
export const ProductsRail = ({ slides, variant }: Props) => {
  const isLight = variant === 'light';
  const [current, setCurrent] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const getCard = () => trackRef.current?.firstElementChild?.firstElementChild as HTMLElement | null;

  const updateFromScroll = useCallback(() => {
    const track = trackRef.current;
    const card = track?.firstElementChild?.firstElementChild as HTMLElement | null;
    if (!track || !card || card.offsetWidth <= 0) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCurrent(Math.round(track.scrollLeft / card.offsetWidth));
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(track.scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      updateFromScroll();
      window.addEventListener('resize', updateFromScroll);
      return () => window.removeEventListener('resize', updateFromScroll);
    }
    const observer = new ResizeObserver(() => updateFromScroll());
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateFromScroll]);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    const card = getCard();
    if (!track || !card) return;
    const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    track.scrollBy({ left: direction * card.offsetWidth, behavior });
  };

  const buttonClass = button({ variant: isLight ? 'sliderLight' : 'sliderDark' });

  return (
    <ActiveSlideContext value={current}>
      <Reveal>
        <div ref={trackRef} onScroll={updateFromScroll} className={trackClass}>
          <div className={rowClass}>
            {slides.map((slide, i) => (
              <SlideIndexContext key={i} value={i}>
                {slide}
              </SlideIndexContext>
            ))}
          </div>
        </div>
      </Reveal>

      <div className={cx(controlsClass, !isLight && controlsDarkClass)}>
        <button
          type="button"
          aria-label="previous slide"
          onClick={() => scrollByCard(-1)}
          disabled={atStart}
          className={cx(buttonClass, atStart ? disabledClass : enabledClass)}
        >
          <span className={arrowClass}>
            <ArrowLeft />
          </span>
        </button>
        <button
          type="button"
          aria-label="next slide"
          onClick={() => scrollByCard(1)}
          disabled={atEnd}
          className={cx(buttonClass, nextMarginClass, atEnd ? disabledClass : enabledClass)}
        >
          <span className={arrowClass}>
            <ArrowRight />
          </span>
        </button>
      </div>
    </ActiveSlideContext>
  );
};
